import { ref, computed } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "../../../consts";
import { useConfirmAction } from "./useConfirmAction";

const parseShowConfig = (show) => ({
  ...show,
  snippets_generation_config: show.snippets_generation_config
    ? JSON.parse(show.snippets_generation_config)
    : null,
});

export function useSnippetShows(token) {
  const toast = useToast();
  const { confirmAction } = useConfirmAction();

  const shows = ref([]);
  const showsLoading = ref(false);

  const fetchShows = async () => {
    try {
      showsLoading.value = true;
      const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { snippetsOnly: true, activeSellerCount: true },
      });
      shows.value = (response.data || []).map(parseShowConfig);
    } catch (error) {
      console.error("Error fetching snippet shows:", error);
    } finally {
      showsLoading.value = false;
    }
  };

  const removeShow = async (show) => {
    try {
      await axios.delete(`${API_BASE_URL}/snippets/shows/${show.show_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      shows.value = shows.value.filter((s) => s.show_id !== show.show_id);
      toast.add({
        severity: "success",
        summary: "Removed",
        detail: "Show removed from the snippets catalog.",
        life: 4000,
      });
    } catch (error) {
      console.error("Error removing snippet show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to remove show.",
        life: 4000,
      });
    }
  };

  const confirmRemoveShow = (show) => {
    confirmAction({
      message: `Remove "${show.show_title}" from the snippets catalog?`,
      header: "Remove Confirmation",
      danger: true,
      accept: () => removeShow(show),
    });
  };

  const generatingOneMoreShowId = ref(null);

  const generateOneMoreSnippet = async (show) => {
    generatingOneMoreShowId.value = show.show_id;
    try {
      await axios.post(
        `${API_BASE_URL}/snippets/shows/${show.show_id}/generate-one`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: "Snippet generation queued for this show.",
        life: 4000,
      });
    } catch (error) {
      console.error("Error generating one more snippet:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to generate snippet.",
        life: 4000,
      });
    } finally {
      generatingOneMoreShowId.value = null;
    }
  };

  const confirmGenerateOneMoreSnippet = (show) => {
    const count = show.snippets_generation_config?.snippets_per_episode ?? 1;
    const pausedNote = show.active_seller_count
      ? ""
      : " No active seller uses this show, so automatic generation is paused for it — this one run still goes through.";
    confirmAction({
      message: `Are you sure you want to process one more episode from this show and generate ${count} more snippet${
        count === 1 ? "" : "s"
      }?${pausedNote}`,
      header: "Generate Confirmation",
      accept: () => generateOneMoreSnippet(show),
    });
  };

  const showAddShowDialog = ref(false);
  const availableShowsToAdd = ref([]);
  const availableShowsLoading = ref(false);
  const showToAdd = ref(null);
  const addShowSubmitting = ref(false);

  const newShowDuration = ref(60);
  const newShowSnippetsPerEpisode = ref(1);
  const newShowEpisodesRangeMode = ref("last");
  const newShowEpisodesRangeCount = ref(5);

  const showAddShowConfirmStep = ref(false);
  const editingShow = ref(null);

  const resetAddShowForm = () => {
    showToAdd.value = null;
    editingShow.value = null;
    newShowDuration.value = 60;
    newShowSnippetsPerEpisode.value = 1;
    newShowEpisodesRangeMode.value = "last";
    newShowEpisodesRangeCount.value = 5;
    showAddShowConfirmStep.value = false;
  };

  const openAddShowDialog = async () => {
    resetAddShowForm();
    showAddShowDialog.value = true;
    availableShowsLoading.value = true;

    try {
      const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { episodeCount: true, excludeSnippetsEnabled: true },
      });
      availableShowsToAdd.value = response.data || [];
    } catch (error) {
      console.error("Error fetching podcast shows:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load shows.",
        life: 4000,
      });
    } finally {
      availableShowsLoading.value = false;
    }
  };

  const addShow = async () => {
    if (!showToAdd.value) return;

    try {
      addShowSubmitting.value = true;
      await axios.post(
        `${API_BASE_URL}/snippets/shows`,
        {
          showId: showToAdd.value.show_id,
          duration: newShowDuration.value,
          snippetsPerEpisode: newShowSnippetsPerEpisode.value,
          episodesRangeForFirstRun:
            newShowEpisodesRangeMode.value === "all"
              ? "all"
              : newShowEpisodesRangeCount.value,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Added",
        detail:
          "Show added to the snippets catalog. Generation has been queued.",
        life: 5000,
      });
      showAddShowDialog.value = false;
      await fetchShows();
    } catch (error) {
      console.error("Error adding snippet show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to add show.",
        life: 4000,
      });
    } finally {
      addShowSubmitting.value = false;
    }
  };

  const openEditShowDialog = (show) => {
    resetAddShowForm();
    editingShow.value = show;
    newShowDuration.value = show.snippets_generation_config?.duration ?? 60;
    newShowSnippetsPerEpisode.value =
      show.snippets_generation_config?.snippets_per_episode ?? 1;
    showAddShowDialog.value = true;
  };

  const saveShowConfig = async () => {
    if (!editingShow.value) return;

    try {
      addShowSubmitting.value = true;
      await axios.put(
        `${API_BASE_URL}/snippets/shows/${editingShow.value.show_id}/config`,
        {
          duration: newShowDuration.value,
          snippetsPerEpisode: newShowSnippetsPerEpisode.value,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Saved",
        detail: "Snippet config updated.",
        life: 4000,
      });
      showAddShowDialog.value = false;
      await fetchShows();
    } catch (error) {
      console.error("Error updating snippet show config:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to update show config.",
        life: 4000,
      });
    } finally {
      addShowSubmitting.value = false;
    }
  };

  // Messages, not booleans: PrimeVue's :min only clamps on blur, so a typed 59
  // stays on screen and the admin needs to be told why Save is dead.
  const durationError = computed(() => {
    const value = newShowDuration.value;
    if (value == null || value < 60) {
      return "Minimum snippet length is 60 seconds.";
    }
    if (value > 180) {
      return "Maximum snippet length is 180 seconds.";
    }
    return null;
  });

  const snippetsPerEpisodeError = computed(() => {
    const value = newShowSnippetsPerEpisode.value;
    if (value == null || value < 1 || value > 5) {
      return "Choose between 1 and 5 snippets per episode.";
    }
    return null;
  });

  const snippetConfigInvalid = computed(
    () => !!durationError.value || !!snippetsPerEpisodeError.value
  );

  const addShowConfirmMessage = computed(() => {
    if (!showToAdd.value) return "";

    const totalEpisodes = showToAdd.value.episode_count ?? 0;
    const episodesToProcess =
      newShowEpisodesRangeMode.value === "all"
        ? totalEpisodes
        : Math.min(newShowEpisodesRangeCount.value, totalEpisodes);
    const totalSnippets = episodesToProcess * newShowSnippetsPerEpisode.value;

    return `This will generate snippets for ${episodesToProcess} episode(s), ${newShowSnippetsPerEpisode.value} snippet(s) each — ${totalSnippets} snippet(s) total. Continue?`;
  });

  const goToAddShowConfirmStep = () => {
    if (!showToAdd.value) return;
    showAddShowConfirmStep.value = true;
  };

  const cancelAddShowConfirmStep = () => {
    showAddShowConfirmStep.value = false;
  };

  return {
    shows,
    showsLoading,
    fetchShows,
    confirmRemoveShow,
    generatingOneMoreShowId,
    confirmGenerateOneMoreSnippet,
    showAddShowDialog,
    availableShowsToAdd,
    availableShowsLoading,
    showToAdd,
    addShowSubmitting,
    newShowDuration,
    newShowSnippetsPerEpisode,
    newShowEpisodesRangeMode,
    newShowEpisodesRangeCount,
    showAddShowConfirmStep,
    addShowConfirmMessage,
    openAddShowDialog,
    addShow,
    goToAddShowConfirmStep,
    cancelAddShowConfirmStep,
    editingShow,
    openEditShowDialog,
    saveShowConfig,
    snippetConfigInvalid,
    durationError,
    snippetsPerEpisodeError,
  };
}
